import React, { Dispatch, SetStateAction, useMemo } from 'react';
import {
    NSInput, NSSelect, NSMultiSelect, NSCard,
} from 'bricks';
import { IOptionAnyValue, IOptionNumberValue } from 'bricks/types';
import './DocumentLibrarySlideout.scss';
import { useDealDirectoryOptions } from 'views/DocumentLibrary/hooks';
import { useParams } from 'react-router-dom';
import { DOCUMENT_TYPE_OPTIONS } from 'views/DocumentLibrary/constants';
import useAuthContext from 'contexts/AuthContext';
import { useGetTimelines } from 'views/Tasks/Timeline/hooks/useGetTimeline';
import { IDocumentTimeline, IUpdateDocumentRequest } from 'views/DocumentLibrary/types';

interface DocumentDetailsProps {
    editMode: boolean;
    documentName: string;
    size?: string;
    format?: string;
    folder?: string;
    dateModified?: string;
    documentType?: string;
    activities?: IDocumentTimeline[];
    updateDocumentForm: IUpdateDocumentRequest;
    setUpdateDocumentForm: Dispatch<SetStateAction<IUpdateDocumentRequest>>;
}

const DocumentDetails: React.FC<DocumentDetailsProps> = ({
    editMode,
    documentName,
    size,
    format,
    folder,
    dateModified,
    documentType,
    activities,
    updateDocumentForm,
    setUpdateDocumentForm,
}) => {
    const { dealId } = useParams<{ dealId: string }>();
    const { selectedOrganizationId } = useAuthContext();

    const { data: timelines = [] } = useGetTimelines({ orgId: selectedOrganizationId!, dealId: Number(dealId) });

    const folderLocationOptions = useDealDirectoryOptions(Number(dealId));
    const activitiesOptions : IOptionNumberValue[] = useMemo(() => {
        if (timelines.length) {
            return timelines.map((timeline) => ({
                label: timeline.name,
                value: Number(timeline.id),
            }));
        }
        return [];
    }, [timelines]);

    return (
        <div className="Content__overview p-2">
            <NSCard className="NSCard--level-3 p-2">
                {editMode && (
                    <>
                        <NSInput
                            name="name"
                            type="text"
                            label="Name"
                            value={updateDocumentForm.name}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setUpdateDocumentForm({ ...updateDocumentForm, name: e.target.value })}
                        />
                        <div className="mt-2 text-white">Folder</div>
                        <NSSelect
                            options={folderLocationOptions}
                            name="folder"
                            placeholder="Home"
                            value={folderLocationOptions.find(option => option.value === updateDocumentForm.directoryId)}
                            onChange={option => setUpdateDocumentForm({ ...updateDocumentForm, directoryId: option.value })}
                            isClearable={false}
                        />
                        <div className="mt-2 text-white">Type</div>
                        <NSSelect
                            options={DOCUMENT_TYPE_OPTIONS}
                            name="documentType"
                            value={DOCUMENT_TYPE_OPTIONS.find(option => (option.value === updateDocumentForm.type))}
                            onChange={option => setUpdateDocumentForm({ ...updateDocumentForm, type: option.value })}
                            isClearable={false}
                        />
                        <div className="mt-2 text-white">Activities</div>
                        <NSMultiSelect
                            fixedPlaceholder="Search to add activities"
                            options={activitiesOptions}
                            selectName="activities"
                            value={activitiesOptions.filter(
                                option => updateDocumentForm.activities && updateDocumentForm.activities.includes(option.value),
                            )}
                            onChange={value => {
                                setUpdateDocumentForm({
                                    ...updateDocumentForm,
                                    activities: value.map((item: IOptionAnyValue) => item.value),
                                });
                            }}
                        />
                    </>
                )}
                {!editMode && (
                    <>
                        <div className="d-flex justify-content-between align-items-center">
                            <h4 className="text-white">Overview</h4>
                        </div>
                        <div className="d-flex flex-column">
                            <div className="d-flex">
                                <span className="text-muted pl-0 col-3">Name:</span>
                                <span className="text-dark">{documentName}</span>
                            </div>
                            <div className="d-flex mt-1">
                                <span className="text-muted pl-0 col-3">Format:</span>
                                <span className="text-dark">{format}</span>
                            </div>
                            <div className="d-flex mt-1">
                                <span className="text-muted pl-0 col-3">Size:</span>
                                <span className="text-dark">{size}</span>
                            </div>
                            <div className="d-flex mt-1">
                                <span className="text-muted pl-0 col-3">Folder:</span>
                                <span className="text-dark">{folder}</span>
                            </div>
                            <div className="d-flex mt-1">
                                <span className="text-muted pl-0 col-3">Type:</span>
                                <span className="text-dark">{documentType || '-'}</span>
                            </div>
                            <div className="d-flex mt-1">
                                <span className="text-muted pl-0 col-3">Activities:</span>
                                <div className="d-flex flex-column">
                                    {activities && activities.length > 0 ? activities.map(task => (
                                        <>
                                            <span key={task.id} className="text-dark">{task.name}</span>
                                            {'\n'}
                                        </>
                                    )) : '-'}
                                </div>
                            </div>
                            <div className="d-flex mt-2">
                                <span className="text-muted pl-0 col-3">Last modified:</span>
                                <span className="text-dark">{dateModified}</span>
                            </div>
                        </div>
                    </>
                )}
            </NSCard>
        </div>
    );
};

export default DocumentDetails;
